import React, { useRef, useCallback } from 'react'
import { useA11yId } from '../utils/a11y'

export type Tab = 'builder' | 'teams' | 'players' | 'h2h' | 'cards' | 'table' | 'ai'

interface TabNavProps {
  activeTab: Tab
  onTabChange: (tab: Tab) => void
  /** ID prefix for tab panels (for aria-controls) */
  panelIdPrefix?: string
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'builder', label: 'Builder' },
  { id: 'teams', label: 'Teams' },
  { id: 'players', label: 'Players' },
  { id: 'h2h', label: 'H2H' },
  { id: 'cards', label: 'Cards' },
  { id: 'table', label: 'Table' },
  { id: 'ai', label: 'AI' },
]

export function TabNav({ activeTab, onTabChange, panelIdPrefix = 'tabpanel' }: TabNavProps) {
  const tablistId = useA11yId('tablist')
  const tabRefs = useRef<Map<Tab, HTMLButtonElement>>(new Map())

  const setTabRef = useCallback((id: Tab, el: HTMLButtonElement | null) => {
    if (el) {
      tabRefs.current.set(id, el)
    } else {
      tabRefs.current.delete(id)
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault()
        newIndex = currentIndex > 0 ? currentIndex - 1 : TABS.length - 1
        break
      case 'ArrowRight':
        e.preventDefault()
        newIndex = currentIndex < TABS.length - 1 ? currentIndex + 1 : 0
        break
      case 'Home':
        e.preventDefault()
        newIndex = 0
        break
      case 'End':
        e.preventDefault()
        newIndex = TABS.length - 1
        break
      default:
        return
    }

    const newTab = TABS[newIndex]
    onTabChange(newTab.id)
    // Focus the new tab
    const tabEl = tabRefs.current.get(newTab.id)
    tabEl?.focus()
  }

  return (
    <div
      id={tablistId}
      className="tabs"
      role="tablist"
      aria-label="Content sections"
    >
      {TABS.map((tab, index) => {
        const isActive = activeTab === tab.id
        return (
          <button
            key={tab.id}
            ref={(el) => setTabRef(tab.id, el)}
            id={`tab-${tab.id}`}
            role="tab"
            aria-selected={isActive}
            aria-controls={`${panelIdPrefix}-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            className={`tab ${isActive ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            type="button"
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Wrapper for tab panel content with proper ARIA attributes
 */
interface TabPanelProps {
  id: Tab
  activeTab: Tab
  children: React.ReactNode
  className?: string
  /** ID prefix matching TabNav's panelIdPrefix */
  idPrefix?: string
}

export function TabPanel({
  id,
  activeTab,
  children,
  className,
  idPrefix = 'tabpanel'
}: TabPanelProps) {
  const isActive = activeTab === id

  if (!isActive) return null

  return (
    <div
      id={`${idPrefix}-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  )
}
